/**
 * The site-wide colour key, remembered across visits.
 *
 * Once the light theme was wired to the home page alone as a test (see the note that used to
 * live in `useSiteTheme`); the product now offers dark/light on every surface, so the state
 * is promoted to one context at the router root. A single source means the toggle in any
 * header — public, app, studio, admin — reads and writes the same preference, and the frame
 * never disagrees with itself about which theme it is.
 *
 * First visit follows the operating system (`prefers-color-scheme`); after that the reader's
 * explicit choice wins and is kept in `localStorage`. No auth material is stored here — the
 * cookie rule (CLAUDE.md §7) is about tokens, and a theme name is neither secret nor a
 * credential.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import type { SiteTheme } from '../../layouts/SiteShell';

const STORAGE_KEY = 'lila.theme';

function isTheme(value: string | null): value is SiteTheme {
  return value === 'dark' || value === 'light';
}

function readInitial(): SiteTheme {
  if (typeof window === 'undefined') return 'dark';

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isTheme(stored)) return stored;

  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

type ThemeContextValue = { readonly theme: SiteTheme; readonly toggle: () => void };

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [theme, setTheme] = useState<SiteTheme>(readInitial);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>;
}

/**
 * Read the current theme and the toggle. Every layout calls this to key its `SiteShell` and
 * wire the header's switch; there is no per-layout copy of the state to drift out of sync.
 */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return value;
}
